import React, { useEffect, useRef, useState } from "react";
import { DeleteOutlined, ArrowRightOutlined } from "@ant-design/icons";
import lang from "../helper/langHelper";
import NormalEditor from "./NormalEditor";
import SingleImageUpload from "./SingleImageUpload";

const getUserProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("admin_userProfile") || "{}");
  } catch {
    return {};
  }
};

const resolveMediaUrl = (value) => {
  if (Array.isArray(value)) {
    return value[0]?.url || value[0]?.thumbUrl || "";
  }

  if (value && typeof value === "object") {
    return value.url || value.thumbUrl || "";
  }

  return value || "";
};

const isVideoAsset = (url = "") =>
  /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);

const LinkedInArticleEditor = ({
  content = "",
  onChange,
  coverMedia,
  onCoverChange,
  disabled = false,
  placeholder = "Start writing your article...",
  minHeight = 360,
  articleLabel,
  authorName,
  authorImage: authorImageProp,
  title = "",
  onTitleChange,
  titlePlaceholder = "Title",
  onPreview,
  onDelete,
  toolbarActions,
}) => {
  const shellRef = useRef(null);
  const titleRef = useRef(null);
  const pendingInlineImageInsertRef = useRef(null);
  const [inlineImageUploadValue, setInlineImageUploadValue] = useState("");
  const profile = getUserProfile();
  const resolvedAuthorName =
    authorName || profile?.user?.name || profile?.name || "Your Name";
  // An article publishes under the CLIENT's own LinkedIn identity, not the
  // staff member currently editing it — callers that know who the client
  // is (this page's live editor, matching what the separate Preview modal
  // already did via authorProfile) should pass authorName/authorImage
  // explicitly. Falls back to the logged-in staff profile only when no
  // client identity was supplied.
  const authorImage =
    authorImageProp ||
    profile?.user?.image ||
    profile?.user?.profileImage ||
    profile?.image ||
    profile?.profileImage ||
    "";
  const resolvedArticleLabel = articleLabel || lang("Individual article");
  const coverUrl = resolveMediaUrl(coverMedia);
  const authorInitial = resolvedAuthorName.trim().charAt(0).toUpperCase();
  const hasVideoCover = isVideoAsset(coverUrl);

  useEffect(() => {
    if (!titleRef.current) {
      return;
    }

    titleRef.current.style.height = "auto";
    titleRef.current.style.height = `${titleRef.current.scrollHeight}px`;
  }, [title]);

  const triggerCoverUpload = () => {
    if (disabled) {
      return;
    }

    const uploadButton = shellRef.current?.querySelector(
      ".linkedin-article-hidden-upload .upload-new-button-mainj"
    );
    uploadButton?.click();
  };

  const triggerInlineImageUpload = (insertImage) => {
    if (disabled) {
      return;
    }

    pendingInlineImageInsertRef.current = insertImage;
    const uploadButton = shellRef.current?.querySelector(
      ".linkedin-article-inline-upload .upload-new-button-mainj"
    );
    uploadButton?.click();
  };

  const handleInlineImageChange = (fileList) => {
    const imageUrl = resolveMediaUrl(fileList);

    if (imageUrl && pendingInlineImageInsertRef.current) {
      pendingInlineImageInsertRef.current(imageUrl);
      pendingInlineImageInsertRef.current = null;
    }

    setInlineImageUploadValue(fileList);
    setTimeout(() => setInlineImageUploadValue(""), 0);
  };

  const authorBlock = (
    <div className="linkedin-article-author-row">
      <div className="linkedin-article-author-avatar">
        {authorImage ? (
          <img src={authorImage} alt={resolvedAuthorName} />
        ) : (
          authorInitial
        )}
      </div>
      <div className="linkedin-article-author-meta">
        <div className="linkedin-article-author-name">
          {resolvedAuthorName}
        </div>
        <div className="linkedin-article-author-type">{resolvedArticleLabel}</div>
      </div>
    </div>
  );

  return (
    <div
      ref={shellRef}
      className={`linkedin-article-composer${
        disabled ? " linkedin-article-composer-readonly" : ""
      }`}
    >
      <NormalEditor
        content={content}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        toolbarPreset="article"
        minHeight={minHeight}
        onRequestInlineImageUpload={triggerInlineImageUpload}
        renderToolbarStart={authorBlock}
        renderToolbarEnd={
          <div className="linkedin-article-actions">
            {toolbarActions}
            <button
              type="button"
              className="linkedin-article-action-icon linkedin-article-delete-button"
              onClick={onDelete}
              disabled={disabled || !onDelete}
              title={lang("Delete")}
              aria-label={lang("Delete")}
            >
              <DeleteOutlined />
            </button>
            <button
              type="button"
              className="linkedin-article-preview-button"
              onClick={onPreview}
              disabled={disabled || !onPreview}
              title={lang("Next")}
              aria-label={lang("Next")}
            >
              <ArrowRightOutlined />
            </button>
          </div>
        }
        renderBeforeEditor={
          <>
            <div className="linkedin-article-cover-shell">
              <div className="linkedin-article-cover-stage">
                {coverUrl ? (
                  hasVideoCover ? (
                    <video
                      className="linkedin-article-cover-media"
                      src={coverUrl}
                      controls
                    />
                  ) : (
                    <img
                      className="linkedin-article-cover-media"
                      src={coverUrl}
                      alt="Article cover"
                    />
                  )
                ) : (
                  <div className="linkedin-article-cover-placeholder">
                    <div className="linkedin-article-cover-placeholder-icon">
                      <span className="linkedin-article-cover-mountain" />
                      <span className="linkedin-article-cover-sun" />
                    </div>
                    <p>{lang("Add a cover image or video to your article.")}</p>
                  </div>
                )}

                {!disabled ? (
                  <button
                    type="button"
                    className="linkedin-article-cover-trigger"
                    onClick={triggerCoverUpload}
                  >
                    {coverUrl
                      ? lang("Change cover from computer")
                      : lang("Upload from computer")}
                  </button>
                ) : null}
              </div>

              {!disabled ? (
                <div className="linkedin-article-hidden-upload">
                  <SingleImageUpload
                    fileType={[
                      "image/png",
                      "image/jpeg",
                      "image/jpg",
                      "image/webp",
                      "video/mp4",
                      "video/quicktime",
                      "video/webm",
                    ]}
                    imageType="blog"
                    onChange={onCoverChange}
                    value={coverMedia}
                    alwaysShowTrigger={true}
                    showUploadList={false}
                  />
                </div>
              ) : null}
            </div>

            <div className="linkedin-article-title-shell">
              <textarea
                ref={titleRef}
                value={title}
                onChange={(event) => onTitleChange?.(event.target.value)}
                disabled={disabled}
                rows={1}
                className="linkedin-article-title-input"
                placeholder={titlePlaceholder}
              />
            </div>

            {!disabled ? (
              <div className="linkedin-article-inline-upload">
                <SingleImageUpload
                  fileType={[
                    "image/png",
                    "image/jpeg",
                    "image/jpg",
                    "image/webp",
                  ]}
                  imageType="blog"
                  onChange={handleInlineImageChange}
                  value={inlineImageUploadValue}
                  alwaysShowTrigger={true}
                  showUploadList={false}
                />
              </div>
            ) : null}
          </>
        }
      />
    </div>
  );
};

export default LinkedInArticleEditor;
